import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { ArrowUpIcon, ArrowDownIcon, EyeIcon, PencilIcon, TrashIcon, PlusIcon, PlusCircleIcon, MinusCircleIcon, ArrowDownTrayIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import SearchBar from "../search/SearchBar";
import StatsCards, { StatsCardsSkeleton } from "../ui/StatsCards";
import { ConfirmModal, Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell, TableEmptyState, TableActionButton, PrimaryButton, SearchContainer, ResultsSummary, Pagination, Modal, TableBodySkeleton } from "../ui";
import InfiniteMultiSelect from "../ui/form/InfiniteMultiSelect";

export default function CRUDListView({
  title,
  Icon,
  columns,
  currentItems,
  searchQuery,
  filterStatus,
  availableStatuses,
  statistics,
  statsConfig,
  sortBy,
  sortOrder,
  totalItems,
  startIndex,
  endIndex,
  currentPage,
  totalPages,
  config,
  confirmationModal,
  customModal,
  successMessage,
  onSearchChange,
  onFilterChange,
  onSort,
  onAdd,
  onView,
  onEdit,
  onDelete,
  onPageChange,
  onConfirmDelete,
  onCancelDelete,
  closeCustomModal,
  handleCustomModalSubmit,
  setCustomModal,
  isDeleting = false,
  toast,
  getDisplayName,
  openCustomModal,
  serverPagination,
  isLoading = false,
  renderExpandedRow,
  customFilters = [],
  customFilterValues = {},
  onCustomFilterChange,
  navigate,
  onLimitChange,
  onExport,
  isExporting = false,
}) {
  const darkMode = useSelector(selectDarkMode);

  // Expanded rows state - track which rows are expanded by their ID
  const [expandedRows, setExpandedRows] = useState({});
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle expand/collapse for a row
  const toggleExpand = (itemId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Reset expanded rows when page, search, or filter changes
  useEffect(() => {
    setExpandedRows({});
  }, [currentPage, searchQuery, filterStatus]);

  // Determine if expand column should be shown
  const showExpandColumn = config?.showExpandColumn !== false && typeof renderExpandedRow === "function";

  // Determine if stats should be shown (only if config enables it)
  const showStats = config?.showStats !== false && (statistics || isLoading);

  return (
    <div className="p-4 lg:p-6">
      {successMessage && <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">✓ {successMessage}</div>}

      {toast?.show && <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>{toast.message}</div>}

      <SearchContainer>
        <div className="flex flex-col md:flex-row gap-4 ">
          <SearchBar value={searchQuery} onChange={onSearchChange} placeholder={`Search ${title.toLowerCase()}...`} showClearButton={true} />
          <div className="flex items-center space-x-3 flex-col sm:flex-row gap-4 sm:gap-0">
            {config?.showStatusFilter !== false && (
              <select
                value={filterStatus}
                onChange={(e) => onFilterChange(e.target.value)}
                className={`w-full sm:w-auto px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              >
                <option value="all">All Status</option>
                {availableStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            )}
            {/* Custom Filters */}
            {customFilters.map((filter) =>
              filter.type === "infinite-multiselect" ? (
                <div key={filter.key} className="w-full sm:w-56">
                  <InfiniteMultiSelect
                    name={filter.key}
                    value={customFilterValues[filter.key] || []}
                    onChange={(e) => onCustomFilterChange(filter.key, e.target.value)}
                    fetchFn={filter.fetchFn}
                    getOptions={filter.getOptions}
                    getTotal={filter.getTotal}
                    extraParams={filter.extraParams || {}}
                    placeholder={filter.placeholder || `All ${filter.label}`}
                  />
                </div>
              ) : (
                <select
                  key={filter.key}
                  value={customFilterValues[filter.key] || "all"}
                  onChange={(e) => onCustomFilterChange(filter.key, e.target.value)}
                  className={`w-full sm:w-auto px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="all">{filter.placeholder || `All ${filter.label}`}</option>
                  {filter.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )
            )}
            {onExport && config?.showExportButton !== false && (
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setExportMenuOpen((prev) => !prev)}
                  disabled={isExporting}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  } disabled:opacity-50`}
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {isExporting ? "Exporting…" : "Export"}
                  <ChevronDownIcon className="w-3 h-3" />
                </button>
                {exportMenuOpen && (
                  <div className={`absolute right-0 mt-1 w-36 rounded-lg shadow-lg border z-20 ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                    {[
                      { label: "Excel (.xlsx)", format: "EXCEL" },
                      { label: "CSV (.csv)", format: "CSV" },
                    ].map(({ label, format }) => (
                      <button
                        key={format}
                        onClick={() => { setExportMenuOpen(false); onExport(format); }}
                        className={`w-full text-left px-4 py-2 text-sm first:rounded-t-lg last:rounded-b-lg transition-colors ${
                          darkMode ? "text-gray-200 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {config?.showAddButton !== false && (
              <PrimaryButton icon={PlusIcon} onClick={onAdd}>
                Add {title}
              </PrimaryButton>
            )}
          </div>
        </div>

        {searchQuery && (
          <div className={`mt-3 text-sm ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>
            Found <span className={darkMode ? "text-white font-medium" : "text-gray-900 font-medium"}>{totalItems}</span> {title.toLowerCase()}
            {totalItems !== 1 ? "s" : ""} matching "{searchQuery}"
          </div>
        )}
      </SearchContainer>

      {/* Statistics Cards - Below filter bar */}
      <div className="mt-4">{showStats && (isLoading && !statistics ? <StatsCardsSkeleton count={statsConfig?.statsOrder?.length || 3} /> : statistics ? <StatsCards statistics={statistics} config={statsConfig} /> : null)}</div>

      <ResultsSummary
        start={serverPagination ? (serverPagination.page - 1) * serverPagination.limit + 1 : startIndex + 1}
        end={serverPagination ? Math.min(serverPagination.page * serverPagination.limit, serverPagination.total) : Math.min(endIndex, totalItems)}
        total={serverPagination?.total || totalItems}
        label={title.toLowerCase()}
      />

      <Table>
        <TableHead>
          <tr>
            {/* Expand Column Header */}
            {showExpandColumn && <TableHeaderCell className="text-center w-16">Expand</TableHeaderCell>}
            {columns.map((column) => (
              <TableHeaderCell key={column.key}>
                {column.sortable !== false ? (
                  <div className="flex items-center space-x-1 cursor-pointer" onClick={() => onSort(column.key)}>
                    <span>{column.label}</span>
                    {sortBy === column.key && (sortOrder === "asc" ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />)}
                  </div>
                ) : (
                  <span>{column.label}</span>
                )}
              </TableHeaderCell>
            ))}
            <TableHeaderCell className="text-center">Actions</TableHeaderCell>
          </tr>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableBodySkeleton rows={serverPagination?.limit || 5} columns={columns.length + (showExpandColumn ? 1 : 0)} />
          ) : currentItems.length > 0 ? (
            currentItems.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow>
                  {/* Expand Toggle Button */}
                  {showExpandColumn && (
                    <TableCell className="text-center w-16">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200
                          ${expandedRows[item.id] ? "bg-blue-500 text-white shadow-md" : darkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}
                        `}
                        title={expandedRows[item.id] ? "Collapse" : "Expand"}
                      >
                        {expandedRows[item.id] ? <MinusCircleIcon className="w-5 h-5" /> : <PlusCircleIcon className="w-5 h-5" />}
                      </button>
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell key={column.key}>{column.render ? column.render(item) : item[column.key] || "—"}</TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center justify-center space-x-2">
                      <TableActionButton icon={EyeIcon} onClick={() => onView(item)} variant="primary" tooltip="View Details" />
                      {config?.showEditButton !== false && <TableActionButton icon={PencilIcon} onClick={() => onEdit(item)} variant="primary" tooltip="Edit" />}
                      {config?.showDeleteButton !== false && <TableActionButton icon={TrashIcon} onClick={() => onDelete(item)} variant="danger" tooltip="Delete" />}
                      {config.customActions &&
                        config.customActions.map((action, index) => (
                          <button key={index} onClick={() => action.onClick(item, openCustomModal, navigate)} className={`p-2 rounded-lg transition-colors ${action.className || "text-gray-600 hover:bg-gray-50"}`} title={action.tooltip}>
                            {action.icon}
                          </button>
                        ))}
                    </div>
                  </TableCell>
                </TableRow>
                {/* Expanded Row Content */}
                {showExpandColumn && expandedRows[item.id] && (
                  <tr>
                    <td colSpan={columns.length + 2} className={`px-6 py-4 ${darkMode ? "bg-gray-900/50" : "bg-gray-50"}`}>
                      <div className="animate-fadeSlideUp">{renderExpandedRow(item)}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <TableEmptyState
              icon={Icon}
              title={`No ${title.toLowerCase()}s found`}
              message={searchQuery || filterStatus !== "all" ? "Try adjusting your search or filter criteria" : `Get started by adding a new ${title.toLowerCase()}`}
              action={!searchQuery && filterStatus === "all" ? onAdd : null}
              actionLabel={`Add ${title}`}
            />
          )}
        </TableBody>
      </Table>

      <div className="mt-6">
        <Pagination
          currentPage={serverPagination?.page || currentPage}
          totalPages={serverPagination?.totalPages || totalPages}
          totalItems={serverPagination?.total || totalItems}
          itemsPerPage={serverPagination?.limit || 10}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
          itemName={title.toLowerCase()}
          isLoading={isLoading}
        />
      </div>

      <ConfirmModal
        isOpen={confirmationModal.isOpen}
        onCancel={onCancelDelete}
        onConfirm={onConfirmDelete}
        title={`Delete ${title}`}
        message={`Are you sure you want to delete "${getDisplayName(confirmationModal.item)}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      {config.customModals &&
        customModal.isOpen &&
        config.customModals[customModal.type] &&
        (() => {
          const modalConfig = config.customModals[customModal.type];

          if (modalConfig.component) {
            const ModalComponent = modalConfig.component;
            return <ModalComponent isOpen={customModal.isOpen} onClose={closeCustomModal} item={customModal.item} onSubmit={handleCustomModalSubmit} />;
          }

          return (
            <Modal isOpen={customModal.isOpen} onClose={closeCustomModal} title={modalConfig.title}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCustomModalSubmit(customModal.data);
                }}
                className="space-y-4"
              >
                {modalConfig.fields.map((field) => (
                  <div key={field.name}>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-200" : "text-[#232526]"}`}>
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === "select" ? (
                      <select
                        value={customModal.data[field.name] || ""}
                        onChange={(e) =>
                          setCustomModal((prev) => ({
                            ...prev,
                            data: { ...prev.data, [field.name]: e.target.value },
                          }))
                        }
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                        required={field.required}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type || "text"}
                        value={customModal.data[field.name] || ""}
                        onChange={(e) =>
                          setCustomModal((prev) => ({
                            ...prev,
                            data: { ...prev.data, [field.name]: e.target.value },
                          }))
                        }
                        placeholder={field.placeholder}
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900"}`}
                        required={field.required}
                      />
                    )}
                  </div>
                ))}

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeCustomModal} className={`px-4 py-2 border rounded-lg transition-colors ${darkMode ? "text-gray-300 border-gray-600 hover:bg-gray-700" : "text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Save
                  </button>
                </div>
              </form>
            </Modal>
          );
        })()}
    </div>
  );
}
